import { type ComponentConfig } from "@puckeditor/core";
import { getWindowJobsiteConfig, type JobsiteBusinessContext } from "../../types/runtime-config";
import { Services, type ServicesProps } from "./services";
import { ServicesFields } from "./services.fields";
import { RegenerableTextField } from "../editor/RegenerableTextField";
import { IconPicker } from "../editor/IconPicker";

export const ServicesConfig: ComponentConfig<ServicesProps> = {
  label: "Services",
  fields: {
    heading: {
      ...ServicesFields.heading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.services.heading"
          label="Heading"
          multiline={false}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheading: {
      ...ServicesFields.subheading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="home.services.subheading"
          label="Subheading"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    layout: ServicesFields.layout,
    columns: ServicesFields.columns,
    variant: ServicesFields.variant,
    showIcons: ServicesFields.showIcons,
    services: {
      ...ServicesFields.services,
      arrayFields: {
        // Spread rather than list: rebuilding this object by hand is what dropped the `href`
        // control, so the renderer could link a service title but nobody could set the link.
        ...ServicesFields.services.arrayFields,
        icon: {
          ...ServicesFields.services.arrayFields.icon,
          render: ({
            value,
            onChange,
          }: {
            value: string | undefined;
            onChange: (val: string | undefined) => void;
          }) => {
            return <IconPicker value={value || ""} onChange={onChange} label="Icon" />;
          },
        },
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
    backgroundColor: ServicesFields.backgroundColor,
  },
  /**
   * The list layout renders a fixed-width stack and never reads the column count. The
   * "(Grid Only)" in the label was a comment, not enforcement.
   */
  resolveFields: (data, { fields }) => {
    if ((data.props as { layout?: string })?.layout !== "list") {
      return fields;
    }

    const { columns: _columns, ...rest } = fields as Record<string, unknown>;
    return rest as typeof fields;
  },
  defaultProps: {
    heading: "Our Professional Services",
    subheading: "Quality service you can trust. Licensed professionals ready to help.",
    layout: "grid",
    columns: "3",
    variant: "cards",
    showIcons: "yes",
    services: [
      {
        title: "Service One",
        description: "Professional service tailored to your needs. Quality workmanship guaranteed.",
        icon: "",
      },
      {
        title: "Service Two",
        description: "Expert solutions for your home or business. Fast, reliable service.",
        icon: "",
      },
      {
        title: "Service Three",
        description:
          "Comprehensive service options to meet your requirements. Satisfaction guaranteed.",
        icon: "",
      },
    ],
  },
  render: ({ id, puck, ...props }) => {
    // Access business data from Puck metadata OR window config (fallback)
    const puckMetadata = puck?.metadata as
      | { business?: JobsiteBusinessContext; pagePaths?: string[] }
      | undefined;
    const windowConfig = getWindowJobsiteConfig();
    const business = puckMetadata?.business || windowConfig?.business;
    // Set of paths that were actually pre-rendered. Used to drop hrefs
    // for service cards whose dedicated /services/X page wasn't
    // generated (plan-cap: only the top-N targeted services get pages).
    // Cards still render — the operator's full menu stays visible —
    // they just lose the link, so visitors aren't routed into a 404.
    const knownPaths = puckMetadata?.pagePaths;
    const filteredServices =
      knownPaths && Array.isArray(props.services)
        ? props.services.map((s) =>
            s.href && !knownPaths.includes(s.href)
              ? { ...s, href: undefined }
              : s,
          )
        : props.services;

    // Default values if not provided in props
    const resolvedProps = {
      ...props,
      services: filteredServices,
      heading:
        props.heading ||
        (business?.city ? `Expert Services in ${business.city}` : "Our Professional Services"),
      subheading:
        props.subheading ||
        (business?.city
          ? `We provide top-rated services to homeowners in ${business.city} and surrounding areas.`
          : "We provide top-rated services to homeowners in your area."),
      variant: props.variant || "cards",
    };

    return <Services id={id} {...resolvedProps} />;
  },
};
